"use client";

import { getClientFunctions } from "@/lib/firebase-client";
import type { Recipe } from "@/lib/types";
import { httpsCallable } from "firebase/functions";

type SetRecipeSharingInput = {
  recipeId: string;
  isPublic: boolean;
  password?: string | null;
  regenerateToken?: boolean;
};
type SetRecipeSharingResult = { success?: boolean; shareToken: string | null };

type GetSharedRecipeInput = { token: string; password?: string };
type GetSharedRecipeResult = {
  recipe: Recipe | null;
  passwordRequired: boolean;
  lockedPreview?: { title: string; imageUrl: string; imageHint: string } | null;
};

/**
 * Generate a unique share token for a recipe
 * Uses a combination of recipe ID and random string
 */
async function callFunctions<TInput, TOutput>(name: string, input: TInput): Promise<TOutput> {
  const functions = getClientFunctions();
  if (!functions) {
    throw new Error("Firebase functions are not available in this environment");
  }
  const callable = httpsCallable<TInput, TOutput>(functions, name);
  const response = await callable(input);
  return response.data;
}

/**
 * Simple hash function for passwords (client-side)
 * Note: In production, password hashing should be done server-side via Firebase Function
 */
export async function getSharedRecipe(token: string, password?: string): Promise<GetSharedRecipeResult> {
  return await callFunctions<GetSharedRecipeInput, GetSharedRecipeResult>("getSharedRecipe", { token, password });
}

/**
 * Enable or disable public sharing for a recipe
 */
export async function updateRecipeSharing(
  recipeId: string,
  isPublic: boolean,
  sharePassword?: string,
): Promise<string | null> {
  const result = await callFunctions<SetRecipeSharingInput, SetRecipeSharingResult>("setRecipeSharing", {
    recipeId,
    isPublic,
    password: sharePassword ?? null,
  });
  return result.shareToken;
}

/**
 * Get the shareable URL for a recipe
 */
export function getRecipeShareUrl(shareToken: string, baseUrl?: string): string {
  const base = baseUrl || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/share/recipe/${shareToken}`;
}

